import axiosInstance from "../config/axios";

export interface HRMSNotificationItem {
  id: string;
  title: string;
  message: string;
  type: "success" | "info" | "warning" | "error";
  category?: "system" | "user" | "security" | "update" | "approval" | "review";
  priority?: "low" | "medium" | "high" | "critical";
  read: boolean;
  actionUrl?: string;
  actionText?: string;
  actionData?: Record<string, any>;
  entityType?: string;
  entityId?: string;
  timestamp: string;
  readAt?: string;
  expiresAt?: string;
  sender?: {
    id: string;
    name: string;
    email: string;
  } | null;
  metadata?: Record<string, any>;
}

interface HRMSNotificationResponse {
  success: boolean;
  data: HRMSNotificationItem[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface HRMSNotificationFilters {
  read?: boolean;
  category?: string;
  priority?: string;
  type?: string;
  entityType?: string;
  entityId?: string;
  page?: number;
  limit?: number;
}

interface HRMSNotificationStats {
  total: number;
  unread: number;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
}

class HRMSNotificationService {
  private baseURL = "/hrms/notifications";

  /**
   * Get HRMS notifications for the current user
   */
  async getNotifications(
    filters: HRMSNotificationFilters = {}
  ): Promise<HRMSNotificationResponse> {
    try {
      const response = await axiosInstance.get(this.baseURL, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching HRMS notifications:", error);
      throw error;
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(
    notificationId: string
  ): Promise<{ success: boolean }> {
    try {
      const response = await axiosInstance.patch(
        `${this.baseURL}/${notificationId}/read`
      );
      return response.data;
    } catch (error) {
      console.error("Error marking HRMS notification as read:", error);
      throw error;
    }
  }

  /**
   * Mark a notification as unread
   */
  async markAsUnread(
    notificationId: string
  ): Promise<{ success: boolean }> {
    try {
      const response = await axiosInstance.patch(
        `${this.baseURL}/${notificationId}/unread`
      );
      return response.data;
    } catch (error) {
      console.error("Error marking HRMS notification as unread:", error);
      throw error;
    }
  }

  /**
   * Mark all HRMS notifications as read
   */
  async markAllAsRead(): Promise<{ success: boolean; count: number }> {
    try {
      const response = await axiosInstance.post(
        `${this.baseURL}/mark-all-read`
      );
      return response.data;
    } catch (error) {
      console.error("Error marking all HRMS notifications as read:", error);
      throw error;
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(
    notificationId: string
  ): Promise<{ success: boolean }> {
    try {
      const response = await axiosInstance.delete(
        `${this.baseURL}/${notificationId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting HRMS notification:", error);
      throw error;
    }
  }

  /**
   * Clear all HRMS notifications
   */
  async clearAllNotifications(): Promise<{ success: boolean; count: number }> {
    try {
      const response = await axiosInstance.post(
        `${this.baseURL}/clear-all`
      );
      return response.data;
    } catch (error) {
      console.error("Error clearing HRMS notifications:", error);
      throw error;
    }
  }

  /**
   * Get notification statistics
   */
  async getStats(): Promise<HRMSNotificationStats> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/stats`);
      return response.data;
    } catch (error) {
      console.error("Error fetching HRMS notification stats:", error);
      throw error;
    }
  }
}

export const hrmsNotificationService = new HRMSNotificationService();
export default hrmsNotificationService;
